'use client'

import Link from 'next/link'
import { useEffect, useState, useCallback } from 'react'
import { HeaderNav } from './Nav'
import { cn } from '@/utilities/ui'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import type { NavItem } from '@/utilities/resolveNavItems'

interface HeaderClientProps {
  navItems: NavItem[]
  siteName?: string
  logoUrl?: string | null
  logoAlt?: string
}

// Dark/transparent header (over hero) uses the white inverse logo so the
// wordmark reads against the dark background; light header uses the default.
// The header's FIXED 208×32 slot uses Steve's hand-anti-aliased PNG exports
// (supplied 2026-08-15, 1x + 2x via srcset) — he found browser SVG rasterization
// too soft here. The CMS-uploaded SVG (site-settings.logo) remains the source
// for fluid-width placements (e.g. the footer). Keep the four
// /placeholders/wilson-logo{,-inverse}-{208,416}.png files in sync with the brand.
const LOGO_SRC = '/placeholders/wilson-logo-208.png'
const LOGO_SRCSET = '/placeholders/wilson-logo-208.png 1x, /placeholders/wilson-logo-416.png 2x'
const INVERSE_LOGO_SRC = '/placeholders/wilson-logo-inverse-208.png'
const INVERSE_LOGO_SRCSET =
  '/placeholders/wilson-logo-inverse-208.png 1x, /placeholders/wilson-logo-inverse-416.png 2x'

function LogoImage({
  url,
  alt,
  dark,
  siteName,
}: {
  url: string | null | undefined
  alt: string
  dark?: boolean
  siteName: string
}) {
  const src = dark ? INVERSE_LOGO_SRC : url ? LOGO_SRC : null
  if (src) {
    // Figma: logo is 156 × 24 on mobile, 208 × 32 from tablet up (same 6.5 aspect).
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        srcSet={dark ? INVERSE_LOGO_SRCSET : LOGO_SRCSET}
        width={208}
        height={32}
        alt={alt}
        className="h-6 md:h-8 w-auto"
      />
    )
  }
  // Text fallback until logos are uploaded to CMS
  return (
    <span
      className={cn(
        'text-body-md font-semibold tracking-tight',
        dark ? 'text-white' : 'text-brand',
      )}
    >
      {siteName}
    </span>
  )
}

export function HeaderClient({
  navItems,
  siteName = 'Wilson Property',
  logoUrl,
  logoAlt = 'Wilson Property',
}: HeaderClientProps) {
  const { headerTheme } = useHeaderTheme()
  const [scrolled, setScrolled] = useState(false)
  const hasHero = headerTheme === 'dark'

  /* Both headers are always mounted (one is faded/translated out), so each reports its
   * own menu state under its own key — a single shared boolean would let the inactive
   * header clobber the active one's `true` with a `false`. */
  const [menuOpen, setMenuOpen] = useState({ transparent: false, sticky: false })
  // Asana 006 (comment) — incremented when the blurred backdrop is tapped on
  // touch layouts; both Nav instances listen and close their menus.
  const [closeSignal, setCloseSignal] = useState(0)
  const anyMenuOpen = menuOpen.transparent || menuOpen.sticky

  const onTransparentMenu = useCallback(
    (open: boolean) =>
      setMenuOpen((m) => (m.transparent === open ? m : { ...m, transparent: open })),
    [],
  )
  const onStickyMenu = useCallback(
    (open: boolean) => setMenuOpen((m) => (m.sticky === open ? m : { ...m, sticky: open })),
    [],
  )

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      {/* Figma Navigation 4114:178153 — the page behind an open menu is covered by an
       *  "Overlay" node (4114:181450 desktop / 4114:184973 tablet / 4114:185100 mobile):
       *  theme/background WHITE at opacity 80 with backdrop-blur/lg (blur/lg = 16px, exactly
       *  Tailwind's `backdrop-blur-lg`). It starts BELOW the header bar so the logo, nav items
       *  and CTA stay sharp, and `pointer-events-none` keeps hover-out and click-through
       *  behaviour unchanged.
       *
       *  It MUST be a sibling of <header>, not a descendant. The sticky header always carries a
       *  `translate-*` utility (`translate-y-0` or `-translate-y-full`), and Tailwind v4 emits
       *  that as the CSS `translate` property — any non-`none` `translate` makes the element a
       *  containing block for `position: fixed` descendants. Rendered inside the header, this
       *  overlay's `top-14 … bottom-0` resolved against the 56/64px bar instead of the viewport
       *  and collapsed to zero height, so no blur ever appeared.
       *
       *  z-30 sits under the header (z-40, which also owns the dropdown panels via its own
       *  stacking context) and over page content. */}
      {anyMenuOpen && (
        /* Below lg the backdrop CATCHES taps and closes the menu (Asana 006
           comment: tapping the blurred background should dismiss it — and the
           tap must not fall through to page links underneath). From lg up it
           stays pointer-events-none so desktop hover-out behaviour is unchanged. */
        <div
          onClick={() => setCloseSignal((n) => n + 1)}
          className="pointer-events-auto lg:pointer-events-none fixed inset-x-0 top-14 bottom-0 z-30 bg-background/80 backdrop-blur-lg motion-safe:animate-[nav-backdrop-in_150ms_ease-out] md:top-16"
          aria-hidden="true"
          data-visual="header-backdrop"
        />
      )}

      {/* Transparent header — always rendered on hero pages (sits over the image) */}
      {hasHero && (
        <header
          className={cn(
            'fixed top-0 left-0 right-0 z-40 transition-opacity duration-300',
            scrolled ? 'opacity-0 pointer-events-none' : 'opacity-100',
          )}
        >
          <div className="mx-auto flex h-14 max-w-[var(--container-content)] items-center justify-between px-[var(--spacing-container)] md:h-16">
            <Link href="/" className="flex shrink-0 items-center">
              <LogoImage url={logoUrl} alt={logoAlt} dark siteName={siteName} />
            </Link>
            <HeaderNav items={navItems} scrolled={false} onMenuOpenChange={onTransparentMenu} closeSignal={closeSignal} />
          </div>
        </header>
      )}

      {/* White sticky header — slides down on scroll (hero pages) or always visible (non-hero) */}
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-40 bg-white transition-all duration-300',
          // The nav backdrop blur already separates the bar from the page, so the
          // shadow is dropped while any menu is open to avoid double-separation.
          scrolled && !anyMenuOpen ? 'shadow-sm' : 'shadow-none',
          hasHero ? (scrolled ? 'translate-y-0' : '-translate-y-full') : 'translate-y-0',
        )}
      >
        <div className="mx-auto flex h-14 max-w-[var(--container-content)] items-center justify-between px-[var(--spacing-container)] md:h-16">
          <Link href="/" className="flex shrink-0 items-center">
            <LogoImage url={logoUrl} alt={logoAlt} siteName={siteName} />
          </Link>
          <HeaderNav items={navItems} scrolled={true} onMenuOpenChange={onStickyMenu} closeSignal={closeSignal} />
        </div>
      </header>
    </>
  )
}
